import { Loader2, Sparkles } from "lucide-react";
import { FC, useEffect, useMemo, useState } from "react";
import { FormattedMessage } from "react-intl";

import { AxiosError } from "axios";
import { useIntl } from "react-intl";
import { DoclistTreeItemSectionsItem } from "~/api/model";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Progress } from "~/components/ui/progress";
import { useChatDocumentParams } from "~/helpers";
import { toast } from "~/hooks/use-toast";
import { trackEvent } from "~/utils/analytics";
import logger from "~/utils/logger";
import { detectSimilarSectionGroups } from "~/utils/textDuplicateDetection";
import { useAnalyzeSectionSimilarity } from "../../hooks/useAnalyzeSectionSimilarity";
import { DeletePageDialog } from "./DeletePageDialog";
import { SideBySideComparisonView } from "./SideBySideComparisonView";
import { TextAnalysisTab } from "./TextAnalysisTab";

interface PossibleDuplicatesModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  sections: DoclistTreeItemSectionsItem[];
  onDeleteSections: (sections: Set<string>) => Promise<void>;
  onDoclistOutdated: () => void;
}

export const PossibleDuplicatesModal: FC<PossibleDuplicatesModalProps> = ({
  open,
  setOpen,
  sections,
  onDeleteSections,
  onDoclistOutdated,
}) => {
  const intl = useIntl();
  const [selectedGroup, setSelectedGroup] = useState<
    DoclistTreeItemSectionsItem[] | null
  >(null);
  const { organizationId, caseId } = useChatDocumentParams();
  const [syncEnabled, setSyncEnabled] = useState(true);

  const [aiDetectedGroups, setAiDetectedGroups] = useState<
    DoclistTreeItemSectionsItem[][]
  >([]);
  const [showNoNewDuplicatesMessage, setShowNoNewDuplicatesMessage] =
    useState(false);
  const [hasRunInitialAnalysis, setHasRunInitialAnalysis] = useState(false);

  const [deletedSectionIds, setDeletedSectionIds] = useState<Set<string>>(
    new Set()
  );

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [sectionsToDelete, setSectionsToDelete] = useState<
    DoclistTreeItemSectionsItem[]
  >([]);

  const visibleSections = useMemo(
    () => sections.filter((section) => !deletedSectionIds.has(section.id)),
    [sections, deletedSectionIds]
  );

  const {
    analyze,
    cancel,
    isAnalyzing: isAnalyzingWithAI,
    progress: aiProgress,
    error: aiAnalysisError,
  } = useAnalyzeSectionSimilarity({
    organizationId: organizationId || "",
    projectId: caseId || "",
    onComplete: (groups) => {
      logger.info("AI analysis complete:", groups);

      const sectionGroups = groups
        .map((group) => {
          return group.sections
            .map((section) => visibleSections.find((s) => s.id === section.id))
            .filter((s): s is DoclistTreeItemSectionsItem => s !== undefined);
        })
        .filter((group) => group.length > 1);

      setAiDetectedGroups(sectionGroups);
      setHasRunInitialAnalysis(true);

      if (sectionGroups.length === 0) {
        setShowNoNewDuplicatesMessage(true);
        setTimeout(() => setShowNoNewDuplicatesMessage(false), 5000);
        toast({
          title: "AI analysis complete",
          description: "No new duplicate groups found.",
        });
      } else {
        toast({
          title: "AI analysis complete",
          description: `Found ${sectionGroups.length} group${sectionGroups.length !== 1 ? "s" : ""} of similar sections.`,
        });
      }
    },
    onError: () => {
      setHasRunInitialAnalysis(true);
    },
  });

  const handleDeleteClick = (sections: DoclistTreeItemSectionsItem[]) => {
    setSectionsToDelete(sections);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    setDeleteDialogOpen(false);
    const sectionIds = new Set(sectionsToDelete.map((s) => s.id));
    const successTitleId =
      sectionsToDelete.length === 1
        ? "docList.deleteSection.success"
        : "docList.deleteSections.success";
    const successDescriptionId =
      sectionsToDelete.length === 1
        ? "docList.deleteSection.successDescription"
        : "docList.deleteSections.successDescription";
    const errorTitleId = "error";
    const errorDescriptionId =
      sectionsToDelete.length === 1
        ? "docList.deleteSection.error"
        : "docList.deleteSections.error";
    try {
      await onDeleteSections(sectionIds);
      const deletedIds = Array.from(sectionIds);
      setDeletedSectionIds((prev) => {
        const updated = new Set(prev);
        deletedIds.forEach((id) => updated.add(id));
        return updated;
      });
      setSelectedGroup((prev) => {
        if (!prev) return prev;
        const remaining = prev.filter((section) => !sectionIds.has(section.id));
        return remaining.length > 0 ? remaining : null;
      });
      setAiDetectedGroups((prev) =>
        prev
          .map((group) =>
            group.filter((section) => !sectionIds.has(section.id))
          )
          .filter((group) => group.length > 1)
      );
      toast({
        title: intl.formatMessage({
          id: successTitleId,
        }),
        description: intl.formatMessage({
          id: successDescriptionId,
        }),
      });
    } catch (error) {
      logger.error("Failed to delete sections:", error);
      if (error instanceof AxiosError && error.response?.status === 409) {
        onDoclistOutdated();
        return;
      }
      toast({
        title: intl.formatMessage({
          id: errorTitleId,
        }),
        description: intl.formatMessage({
          id: errorDescriptionId,
        }),
      });
      logger.error("Failed to delete sections:", error);
    } finally {
      setSectionsToDelete([]);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSectionsToDelete([]);
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen && isAnalyzingWithAI) {
      return;
    }

    setOpen(newOpen);
    if (newOpen) {
      trackEvent({ eventName: "app/doc_list/duplicates_modal.open" });
    } else {
      setSelectedGroup(null);
      setAiDetectedGroups([]);
      setShowNoNewDuplicatesMessage(false);
      setHasRunInitialAnalysis(false);
      setDeletedSectionIds(new Set());
    }
  };

  const handleAnalyzeWithAI = () => {
    if (!organizationId || !caseId || visibleSections.length === 0) return;

    trackEvent({ eventName: "app/doc_list/duplicates_modal.analyze_with_ai" });

    const sectionIds = visibleSections.map((s) => s.id);
    analyze(sectionIds);
  };

  useEffect(() => {
    if (!open || visibleSections.length === 0) return;

    const groups = detectSimilarSectionGroups(visibleSections);
    const allDetectedGroups = [...groups.identical, ...groups.verySimilar];
    const nextGroup = allDetectedGroups[0] || null;

    setSelectedGroup((prev) => {
      const prevIds =
        prev
          ?.map((s) => s.id)
          .sort()
          .join(",") ?? "";
      const nextIds =
        nextGroup
          ?.map((s) => s.id)
          .sort()
          .join(",") ?? "";
      return prevIds === nextIds ? prev : nextGroup;
    });
  }, [open, visibleSections]);

  useEffect(() => {
    if (
      open &&
      !hasRunInitialAnalysis &&
      !isAnalyzingWithAI &&
      organizationId &&
      caseId &&
      visibleSections.length > 0
    ) {
      const sectionIds = visibleSections.map((s) => s.id);
      analyze(sectionIds);
    }
  }, [
    open,
    hasRunInitialAnalysis,
    isAnalyzingWithAI,
    organizationId,
    caseId,
    visibleSections,
    analyze,
  ]);

  useEffect(() => {
    // Cancel ongoing analysis when modal closes
    if (!open && isAnalyzingWithAI) {
      cancel();
    }
  }, [open, isAnalyzingWithAI, cancel]);

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent
          className="flex h-[calc(100vh-32px)] w-[calc(100vw-32px)] max-w-none flex-col"
          aria-describedby="duplicates-description"
        >
          <DialogHeader>
            <DialogTitle>
              <FormattedMessage
                id="docList.duplicates.title"
                defaultMessage="Duplicates"
              />
            </DialogTitle>
            <DialogDescription id="duplicates-description" className="sr-only">
              <FormattedMessage
                id="docList.duplicates.description"
                defaultMessage="Find and manage duplicate sections using text-based detection."
              />
            </DialogDescription>
            {isAnalyzingWithAI && (
              <div className="mt-2 space-y-2">
                <p className="text-sm text-muted-foreground">
                  {aiProgress.message}
                </p>
                <Progress
                  value={(aiProgress.current / aiProgress.total) * 100}
                  className="h-2"
                />
              </div>
            )}
            {aiAnalysisError && (
              <div className="mt-2 text-sm text-destructive">
                {aiAnalysisError}
              </div>
            )}
          </DialogHeader>

          <div className="mt-4 flex flex-1 min-h-0 gap-4">
            <div className="flex-none basis-[280px] min-w-0">
              <TextAnalysisTab
                className="min-h-0 max-h-full overflow-y-auto space-y-2"
                sections={visibleSections}
                onDeleteClick={handleDeleteClick}
                onSelectGroup={setSelectedGroup}
                selectedGroup={selectedGroup}
                aiDetectedGroups={aiDetectedGroups}
                isAnalyzing={isAnalyzingWithAI}
                showNoNewDuplicatesMessage={showNoNewDuplicatesMessage}
              />
            </div>

            {selectedGroup ? (
              <div className="flex-1 min-w-0 border-l pl-4 overflow-hidden">
                <SideBySideComparisonView
                  sections={selectedGroup}
                  onDeleteClick={handleDeleteClick}
                  syncEnabled={syncEnabled}
                  onSyncEnabledChange={setSyncEnabled}
                />
              </div>
            ) : (
              <div className="flex flex-1 items-center justify-center text-muted-foreground">
                <FormattedMessage
                  id="docList.duplicates.noDuplicates"
                  defaultMessage="No duplicate groups found"
                />
              </div>
            )}
          </div>

          <DialogFooter className="sm:justify-between">
            <div className="flex items-center gap-2">
              {hasRunInitialAnalysis && (
                <Button
                  onClick={handleAnalyzeWithAI}
                  disabled={isAnalyzingWithAI || visibleSections.length === 0}
                  variant="outline"
                  size="sm"
                  className="gap-2"
                >
                  {isAnalyzingWithAI ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <FormattedMessage
                        id="docList.duplicates.analyzing"
                        defaultMessage="Analyzing..."
                      />
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-4 w-4" />
                      <FormattedMessage
                        id="docList.duplicates.analyzeWithAI"
                        defaultMessage="Analyze with AI"
                      />
                    </>
                  )}
                </Button>
              )}
            </div>
            <div className="flex items-center gap-2">
              {isAnalyzingWithAI && (
                <p className="text-xs text-muted-foreground">
                  <FormattedMessage
                    id="docList.duplicates.analysisInProgress"
                    defaultMessage="Please wait for analysis to complete..."
                  />
                </p>
              )}
              <Button
                onClick={() => setOpen(false)}
                variant="outline"
                disabled={isAnalyzingWithAI}
              >
                <FormattedMessage id="close" defaultMessage="Close" />
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DeletePageDialog
        open={deleteDialogOpen}
        setOpen={setDeleteDialogOpen}
        onDelete={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        type="section"
        selectedCount={sectionsToDelete.length}
        name={
          sectionsToDelete.length === 1 ? sectionsToDelete[0]?.name : undefined
        }
        sectionNames={
          sectionsToDelete.length > 1
            ? sectionsToDelete.map((s) => s.name)
            : undefined
        }
      />
    </>
  );
};

export default PossibleDuplicatesModal;
